import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Mock step content ─────────────────────────────────────────────────────

const STEPS = [
  {
    label: 'ASSESS',
    title: 'Self-Assessment',
    subtitle: 'What kind of trader are you?',
    cards: ['New Trader', 'Backtesting', 'Proven'],
  },
  {
    label: 'STRATEGY',
    title: 'Choose Your Weapon',
    subtitle: 'Which model fits your edge?',
    cards: ['TCL Model', 'SMOG Model'],
  },
  {
    label: 'FIRM',
    title: 'Select Your Arena',
    subtitle: 'Pick your prop firm and account.',
    cards: ['Apex', 'Topstep', 'Tradeify', 'MFF'],
  },
  {
    label: 'PROTOCOL',
    title: 'Your Prescription',
    subtitle: 'Armed and ready.',
    cards: [],
    isPrescription: true,
  },
];

// ─── SVG Mountain silhouettes ───────────────────────────────────────────────

// Four layers of mountains that span the full journey width (400vw)
// Each layer is a simple bezier path

function MountainLayer({
  step,
  layerIndex,
  speed,
  color,
  opacity,
  yOffset,
}: {
  step: number;
  layerIndex: number;
  speed: number;
  color: string;
  opacity: number;
  yOffset: number;
}) {
  // Each layer shifts left as step increases, at its own parallax speed
  const translateX = -step * 100 * speed; // vw units

  // Different mountain profiles per layer
  const paths = [
    // Layer 0 (farthest) — gentle rolling hills
    'M0,180 C80,120 160,160 240,100 C320,40 400,130 480,90 C560,50 640,140 720,110 C800,80 880,150 960,100 C1040,50 1120,130 1200,80 C1280,30 1360,120 1440,90 C1520,60 1600,140 1680,100 C1760,60 1840,130 1920,80 L1920,400 L0,400 Z',
    // Layer 1 — sharper peaks
    'M0,200 C40,180 80,120 140,160 C200,200 240,80 320,140 C400,200 440,60 520,130 C600,200 640,70 720,150 C800,220 840,90 920,160 C1000,220 1040,80 1120,140 C1200,190 1240,60 1320,130 C1400,200 1440,90 1520,160 C1600,210 1640,80 1720,140 C1800,200 1840,100 1920,150 L1920,400 L0,400 Z',
    // Layer 2 — jagged foreground ridges
    'M0,220 L60,180 L100,210 L160,150 L200,200 L260,130 L320,190 L380,140 L440,210 L500,160 L560,200 L620,120 L680,190 L740,150 L800,210 L860,140 L920,200 L980,130 L1040,190 L1100,150 L1160,210 L1220,160 L1280,200 L1340,120 L1400,180 L1460,140 L1520,200 L1580,160 L1640,210 L1700,150 L1760,190 L1820,140 L1920,180 L1920,400 L0,400 Z',
    // Layer 3 (nearest) — dark ground line with undulation
    'M0,260 C80,240 160,270 240,250 C320,230 400,265 480,245 C560,225 640,260 720,240 C800,220 880,255 960,240 C1040,220 1120,260 1200,235 C1280,215 1360,250 1440,235 C1520,220 1600,255 1680,240 C1760,225 1840,260 1920,240 L1920,400 L0,400 Z',
  ];

  return (
    <motion.div
      className="absolute bottom-0 left-0 pointer-events-none"
      style={{
        width: '500vw',
        height: '45vh',
      }}
      animate={{ x: `${translateX}vw` }}
      transition={{ duration: 0.8, ease: [0.4, 0, 0.15, 1] }}
    >
      <svg
        viewBox="0 0 1920 400"
        preserveAspectRatio="none"
        className="w-full h-full"
        style={{ opacity }}
      >
        <path d={paths[layerIndex]} fill={color} />
      </svg>
    </motion.div>
  );
}

// ─── Sky gradient layer ─────────────────────────────────────────────────────

// One wide gradient that shifts with parallax — reveals different "sky zones"
function SkyLayer({ step }: { step: number }) {
  // The sky transitions from deep night → pre-dawn → amber sunrise
  const skyStops = [
    { stop: '0%', colors: ['#040608', '#060A12', '#080D18', '#0A1020'] },   // night
    { stop: '25%', colors: ['#060A12', '#0A0F1A', '#0E1325', '#121830'] },  // deep night
    { stop: '50%', colors: ['#0C0A14', '#140E1A', '#1A1220', '#201828'] },  // pre-dawn violet
    { stop: '75%', colors: ['#120A0A', '#1A0E0A', '#221408', '#2A1A0A'] },  // forge amber
  ];

  // Interpolate sky colors based on step
  const t = step / (STEPS.length - 1);
  const idx = Math.min(Math.floor(t * 3), 2);
  const frac = (t * 3) - idx;

  const lerp = (a: string, b: string, f: number) => {
    const parse = (hex: string) => [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
    const ca = parse(a);
    const cb = parse(b);
    const r = Math.round(ca[0] + (cb[0] - ca[0]) * f);
    const g = Math.round(ca[1] + (cb[1] - ca[1]) * f);
    const b_ = Math.round(ca[2] + (cb[2] - ca[2]) * f);
    return `rgb(${r},${g},${b_})`;
  };

  const top = lerp(skyStops[idx].colors[0], skyStops[Math.min(idx + 1, 3)].colors[0], frac);
  const mid1 = lerp(skyStops[idx].colors[1], skyStops[Math.min(idx + 1, 3)].colors[1], frac);
  const mid2 = lerp(skyStops[idx].colors[2], skyStops[Math.min(idx + 1, 3)].colors[2], frac);
  const bottom = lerp(skyStops[idx].colors[3], skyStops[Math.min(idx + 1, 3)].colors[3], frac);

  return (
    <motion.div
      className="fixed inset-0 z-0"
      animate={{
        background: `linear-gradient(180deg, ${top} 0%, ${mid1} 30%, ${mid2} 65%, ${bottom} 100%)`,
      }}
      transition={{ duration: 1.0, ease: 'easeInOut' }}
    />
  );
}

// ─── Stars layer ────────────────────────────────────────────────────────────

function StarsLayer({ step }: { step: number }) {
  // Stars fade as dawn approaches
  const starOpacity = Math.max(0, 1 - step * 0.35);

  // Generate deterministic star positions
  const stars = Array.from({ length: 80 }, (_, i) => ({
    x: ((i * 7919 + 3571) % 100),
    y: ((i * 6271 + 1237) % 60),
    size: 1 + (i % 3),
    delay: (i % 5) * 0.3,
    twinkle: i % 4 === 0,
  }));

  return (
    <motion.div
      className="fixed inset-0 z-[1] pointer-events-none overflow-hidden"
      animate={{ opacity: starOpacity }}
      transition={{ duration: 1.2 }}
    >
      {stars.map((star, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size,
          }}
          animate={
            star.twinkle
              ? { opacity: [0.3, 0.9, 0.3] }
              : { opacity: 0.4 + (i % 5) * 0.12 }
          }
          transition={
            star.twinkle
              ? { repeat: Infinity, duration: 2 + star.delay, ease: 'easeInOut' }
              : {}
          }
        />
      ))}
    </motion.div>
  );
}

// ─── Horizon glow (sunrise reveal on final step) ───────────────────────────

function HorizonGlow({ step }: { step: number }) {
  const isPrescription = step === STEPS.length - 1;

  return (
    <motion.div
      className="fixed bottom-[30%] left-1/2 -translate-x-1/2 z-[2] pointer-events-none"
      style={{
        width: '120vw',
        height: '60vh',
      }}
      animate={{
        opacity: isPrescription ? 1 : step * 0.08,
        scale: isPrescription ? 1.2 : 0.8 + step * 0.05,
      }}
      transition={{ duration: isPrescription ? 1.2 : 0.8, ease: 'easeOut' }}
    >
      <div
        className="w-full h-full"
        style={{
          background: isPrescription
            ? 'radial-gradient(ellipse at 50% 70%, rgba(232, 148, 58, 0.2) 0%, rgba(232, 148, 58, 0.08) 30%, transparent 65%)'
            : 'radial-gradient(ellipse at 50% 70%, rgba(232, 148, 58, 0.04) 0%, transparent 50%)',
        }}
      />
    </motion.div>
  );
}

// ─── Progress tracker (horizontal journey dots) ─────────────────────────────

function JourneyTracker({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center gap-1">
      {STEPS.map((step, i) => {
        const isComplete = i < currentStep;
        const isCurrent = i === currentStep;
        return (
          <div key={step.label} className="flex items-center">
            <motion.div
              className={`
                relative w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-mono font-medium border
                ${isComplete ? 'bg-[#E8943A]/20 border-[#E8943A]/40 text-[#E8943A]' : ''}
                ${isCurrent ? 'bg-white/10 border-white/20 text-white' : ''}
                ${!isComplete && !isCurrent ? 'bg-white/[0.02] border-white/[0.04] text-white/15' : ''}
              `}
              animate={isCurrent ? { scale: [1, 1.08, 1] } : {}}
              transition={isCurrent ? { repeat: Infinity, duration: 2, ease: 'easeInOut' } : {}}
            >
              {isComplete ? '✓' : i + 1}
            </motion.div>
            {i < STEPS.length - 1 && (
              <motion.div
                className="w-10 h-px mx-1"
                animate={{
                  backgroundColor: isComplete
                    ? 'rgba(232, 148, 58, 0.3)'
                    : 'rgba(255,255,255,0.04)',
                }}
                transition={{ duration: 0.6 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Prescription card with sunrise reveal ──────────────────────────────────

function PrescriptionCard() {
  return (
    <motion.div
      className="relative w-full max-w-[480px]"
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{
        type: 'spring' as const,
        stiffness: 180,
        damping: 22,
        delay: 0.5,
      }}
    >
      {/* Breathing amber glow — unique to prescription */}
      <motion.div
        className="absolute -inset-4 rounded-2xl pointer-events-none z-0"
        animate={{
          boxShadow: [
            '0 0 40px rgba(232, 148, 58, 0.08), 0 0 80px rgba(232, 148, 58, 0.04)',
            '0 0 60px rgba(232, 148, 58, 0.15), 0 0 120px rgba(232, 148, 58, 0.06)',
            '0 0 40px rgba(232, 148, 58, 0.08), 0 0 80px rgba(232, 148, 58, 0.04)',
          ],
        }}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
      />

      <div className="relative bg-white/[0.03] border border-[#E8943A]/20 rounded-xl p-8 backdrop-blur-md overflow-hidden z-10">
        {/* Inner warm gradient */}
        <div
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(232, 148, 58, 0.06) 0%, transparent 60%)',
          }}
        />

        <div className="relative z-10">
          <motion.p
            className="text-[10px] font-mono text-[#E8943A]/60 tracking-[0.2em] uppercase mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            Paladin Protocol
          </motion.p>

          <motion.div
            className="flex items-baseline gap-3 mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <span className="text-[11px] font-mono text-white/40 uppercase tracking-wider">
              Risk / Trade
            </span>
            <span className="text-3xl font-mono font-bold text-white">$187</span>
            <span className="text-lg font-mono text-white/50">1.5%</span>
          </motion.div>

          <div className="space-y-3 mb-6">
            {['Max 2 contracts per entry', 'Hard stop at daily loss limit', 'No scaling into losers'].map(
              (rule, i) => (
                <motion.div
                  key={rule}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.1 + i * 0.12, duration: 0.4 }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#E8943A]/60" />
                  <span className="text-sm font-mono text-white/50">{rule}</span>
                </motion.div>
              ),
            )}
          </div>

          <motion.div
            className="h-px bg-gradient-to-r from-transparent via-[#E8943A]/20 to-transparent"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1.5, duration: 0.6 }}
          />

          <motion.p
            className="text-[10px] font-mono text-white/20 mt-4 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.7 }}
          >
            Generated Apr 14, 2026. Regenerate if stats change.
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Step content card ──────────────────────────────────────────────────────

function StepCard({ label }: { label: string }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-6 py-5 backdrop-blur-md">
      <span className="text-sm font-mono text-white/50">{label}</span>
    </div>
  );
}

// ─── Main Preview Component ─────────────────────────────────────────────────

export function TransitionPreview2() {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);

  const step = STEPS[currentStep];
  const isPrescription = !!step.isPrescription;

  const goToStep = useCallback(
    (next: number) => {
      if (next < 0 || next >= STEPS.length || next === currentStep) return;
      setDirection(next > currentStep ? 1 : -1);
      setCurrentStep(next);
    },
    [currentStep],
  );

  // Horizontal slide variants — content arcs slightly on entry/exit
  const contentVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 200 : -200,
      y: 15,
      opacity: 0,
    }),
    center: {
      x: 0,
      y: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -200 : 200,
      y: 15,
      opacity: 0,
    }),
  };

  // Firm cards enter as a staggered formation
  const isFirmStep = currentStep === 2;

  return (
    <div className="relative min-h-screen overflow-hidden select-none">
      {/* ── Layer A: Sky gradient ── */}
      <SkyLayer step={currentStep} />

      {/* ── Stars ── */}
      <StarsLayer step={currentStep} />

      {/* ── Horizon glow ── */}
      <HorizonGlow step={currentStep} />

      {/* ── Mountain layers (parallax) ── */}
      <div className="fixed inset-0 z-[3] pointer-events-none overflow-hidden">
        {/* Prescription: mountains split apart */}
        <motion.div
          animate={isPrescription ? { y: -80, opacity: 0 } : { y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.15, 1] }}
        >
          <MountainLayer step={currentStep} layerIndex={0} speed={0.15} color="#0A0E18" opacity={0.5} yOffset={0} />
          <MountainLayer step={currentStep} layerIndex={1} speed={0.3} color="#0D1220" opacity={0.65} yOffset={0} />
        </motion.div>

        <motion.div
          animate={isPrescription ? { y: 60, opacity: 0 } : { y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.15, 1] }}
        >
          <MountainLayer step={currentStep} layerIndex={2} speed={0.5} color="#111828" opacity={0.8} yOffset={0} />
          <MountainLayer step={currentStep} layerIndex={3} speed={0.7} color="#0A0F1A" opacity={1} yOffset={0} />
        </motion.div>
      </div>

      {/* ── Content layer ── */}
      <div className="relative z-10 min-h-screen flex flex-col items-center pt-12 px-8">
        {/* Journey tracker */}
        <JourneyTracker currentStep={currentStep} />

        {/* Tracker shimmer on final step */}
        {isPrescription && (
          <motion.div
            className="absolute top-[52px] left-1/2 -translate-x-1/2 w-[280px] h-[2px] pointer-events-none"
            initial={{ x: '-140px', opacity: 0 }}
            animate={{ x: '140px', opacity: [0, 1, 0] }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeInOut' }}
          >
            <div className="w-16 h-full bg-gradient-to-r from-transparent via-[#E8943A]/60 to-transparent" />
          </motion.div>
        )}

        {/* Step label */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`label-${currentStep}`}
            custom={direction}
            initial={{ opacity: 0, x: direction > 0 ? 40 : -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction > 0 ? -40 : 40 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
            className="text-center mt-8 mb-8"
          >
            <p className="text-[10px] font-mono text-[#E8943A]/50 tracking-[0.3em] uppercase mb-2">
              {step.label}
            </p>
            <h1 className="text-2xl font-mono font-bold text-white/90 tracking-wide">
              {step.title}
            </h1>
            <p className="text-sm font-mono text-white/30 mt-2">{step.subtitle}</p>
          </motion.div>
        </AnimatePresence>

        {/* Step content */}
        <div className="relative w-full max-w-[520px] min-h-[320px] flex items-center justify-center">
          <AnimatePresence mode="wait" custom={direction}>
            {isPrescription ? (
              <motion.div
                key="prescription"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full flex justify-center"
              >
                <PrescriptionCard />
              </motion.div>
            ) : (
              <motion.div
                key={`step-${currentStep}`}
                custom={direction}
                variants={contentVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.55,
                  ease: [0.7, 0, 0.3, 1],
                }}
                className="w-full"
              >
                <div className="grid gap-3">
                  {step.cards.map((card, i) => (
                    <motion.div
                      key={card}
                      initial={isFirmStep ? { opacity: 0, x: 40, y: (i - 1.5) * 8 } : { opacity: 0 }}
                      animate={{ opacity: 1, x: 0, y: 0 }}
                      transition={{
                        delay: isFirmStep ? 0.2 + i * 0.06 : 0.1 + i * 0.06,
                        duration: 0.4,
                        ease: 'easeOut',
                      }}
                    >
                      <StepCard label={card} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center gap-4 mt-12">
          <button
            onClick={() => goToStep(currentStep - 1)}
            disabled={currentStep === 0}
            className={`
              px-5 py-2.5 rounded-lg text-sm font-mono transition-all duration-200
              ${currentStep === 0
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-white/[0.04] text-white/50 border border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70'}
            `}
          >
            Back
          </button>

          <div className="text-[10px] font-mono text-white/20 tracking-wider">
            {currentStep + 1} / {STEPS.length}
          </div>

          <button
            onClick={() => goToStep(currentStep + 1)}
            disabled={currentStep === STEPS.length - 1}
            className={`
              px-5 py-2.5 rounded-lg text-sm font-mono transition-all duration-200
              ${currentStep === STEPS.length - 1
                ? 'bg-white/[0.02] text-white/10 border border-white/[0.03] cursor-not-allowed'
                : 'bg-[#E8943A]/10 text-[#E8943A]/80 border border-[#E8943A]/20 hover:bg-[#E8943A]/20 hover:text-[#E8943A]'}
            `}
          >
            {currentStep === STEPS.length - 2 ? 'Reveal Protocol' : 'Next'}
          </button>
        </div>

        {/* Concept label */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-full px-5 py-2 backdrop-blur-md">
            <span className="text-[10px] font-mono text-white/30 tracking-[0.2em] uppercase">
              Concept 2 — The Horizon March
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
